import React, { useMemo, useState } from "react";
import "./ComparisonTable.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { TablePagination } from "@mui/material";

function formatAmount(value) {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function ConvertTable({ data = [], totals = null }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const renderCellItems = (items, formatter = (x) => x) => {
        if (!items || items.length === 0) return "";
        if (items.length === 1) return formatter(items[0]);

        return (
            <ul className="cell-list">
                {items.map((item, i) => (
                    <li key={i}>{formatter(item)}</li>
                ))}
            </ul>
        );
    };

    const joinCellItems = (items) => {
        if (!items || items.length === 0) return "";
        return items.join(" | ");
    };

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const fioA = (a["ФИО_pdf_финал"] || [""])[0] || "";
            const fioB = (b["ФИО_pdf_финал"] || [""])[0] || "";
            return fioA.localeCompare(fioB, "ru");
        });
    }, [data]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Конвертация");

        worksheet.columns = [
            { key: "fio", width: 34 },
            { key: "iin", width: 16 },
            { key: "accounts", width: 42 },
            { key: "sum", width: 16 }
        ];

        worksheet.getCell("A1").value = "ИТОГО:";
        worksheet.getCell("A2").value = "Всего ИИН:";
        worksheet.getCell("B2").value = totals?.total_iins ?? 0;

        worksheet.getCell("A3").value = "Итого PDF:";
        worksheet.getCell("B3").value = totals?.total_pdf ?? 0;

        ["A1", "A2", "A3"].forEach((cellRef) => {
            worksheet.getCell(cellRef).font = { bold: true };
            worksheet.getCell(cellRef).alignment = { horizontal: "left" };
        });

        worksheet.getCell("B2").alignment = { horizontal: "left" };
        worksheet.getCell("B3").alignment = { horizontal: "left" };
        worksheet.getCell("B3").numFmt = "#,##0.00";

        const headerRowNumber = 6;
        const headerRow = worksheet.getRow(headerRowNumber);

        headerRow.values = ["ФИО", "ИИН", "Счета", "Сумма"];

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "1F4E78" }
            };
            cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true
            };
        });

        headerRow.height = 28;

        sortedData.forEach((row) => {
            const excelRow = worksheet.addRow([
                joinCellItems(row["ФИО_pdf_финал"] || []),
                row["ИИН_pdf"] || "",
                joinCellItems(row["Счета_pdf_финал"] || []),
                Number(row["Сумма_pdf"] || 0)
            ]);

            excelRow.getCell(4).numFmt = "#,##0.00";

            excelRow.eachCell((cell) => {
                cell.alignment = {
                    vertical: "top",
                    wrapText: true
                };
            });

            excelRow.getCell(4).fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "F3FAF5" }
            };
        });

        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber >= headerRowNumber) {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin", color: { argb: "D9D9D9" } },
                        left: { style: "thin", color: { argb: "D9D9D9" } },
                        bottom: { style: "thin", color: { argb: "D9D9D9" } },
                        right: { style: "thin", color: { argb: "D9D9D9" } }
                    };
                });
            }
        });

        worksheet.autoFilter = {
            from: { row: headerRowNumber, column: 1 },
            to: { row: headerRowNumber, column: 4 }
        };

        worksheet.views = [
            {
                state: "frozen",
                ySplit: headerRowNumber
            }
        ];

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const now = new Date();
        const stamp =
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}` +
            `_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;

        saveAs(blob, `convert_pp_${stamp}.xlsx`);
    };

    if (!data.length) return null;

    return (
        <section className="comparison-section">
            <div className="comparison-header-card">
                <div className="comparison-header-main">
                    <div>
                        <div className="comparison-kicker">Результаты</div>
                        <h3 className="comparison-title">Конвертация платежных поручений</h3>
                    </div>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="comparison-export-btn"
                    >
                        Скачать Excel
                    </button>
                </div>

                {totals && (
                    <div className="comparison-summary-grid comparison-summary-grid-convert">
                        <div className="summary-card">
                            <span className="summary-label">Всего ИИН</span>
                            <strong>{totals.total_iins ?? 0}</strong>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Итого PDF</span>
                            <strong>{formatAmount(totals.total_pdf)}</strong>
                        </div>
                    </div>
                )}
            </div>

            <div className="comparison-table-card">
                <div className="comparison-table-scroll">
                    <table className="comparison-table comparison-table-convert">
                        <thead>
                            <tr>
                                <th>ФИО</th>
                                <th>ИИН</th>
                                <th>Счета</th>
                                <th>Сумма</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, index) => (
                                <tr key={index} className="row-ok">
                                    <td>{renderCellItems(row["ФИО_pdf_финал"] || [])}</td>
                                    <td>{row["ИИН_pdf"] || ""}</td>
                                    <td>{renderCellItems(row["Счета_pdf_финал"] || [])}</td>
                                    <td className="num">{formatAmount(row["Сумма_pdf"])}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="comparison-pagination-wrap">
                    <TablePagination
                        component="div"
                        count={sortedData.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Строк на странице:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                    />
                </div>
            </div>
        </section>
    );
}

export default ConvertTable;